import * as React from 'react';

import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { Box, Card, Stack, Typography } from '@mui/material';

import { StoreDatatable } from '@europrocurement/flexy-datatable';
import { dissociateIcon } from '@europrocurement/l2d-icons';
import { DataSource, getDataThunkType } from '@europrocurement/l2d-redux-utils';
import { FactureAchat, ReminderPublisherApiObject } from '@europrocurement/l2d-domain';
import {
    FlexyIconButton,
    footerHeight,
    TopbarHeight,
    useL2DTheme,
} from '@europrocurement/flexy-components';
import { navigateToFacture } from '../../../../../Achats/components/lists/FactureAchatList';

import AssociateInvoiceButton from './AssociateInvoiceButton';
import GlobalEligibilitySummary from './Eligibility/GlobalEligibilitySummary';
import SingleInvoiceEligibilityInfo from './Eligibility/SingleInvoiceEligibilityInfo';
import useDissociateInvoiceReminderPublisher from '../hooks/useDissociateInvoiceReminderPublisher';
import { invoiceColumns } from '../constants/columnDefinitions';

/**
 * Props for InvoicesList component
 */
type InvoicesListProps = {
    /** Data source for invoices */
    dataSource: DataSource<FactureAchat>;
    /** Function to fetch invoices */
    fetchInvoices: getDataThunkType<FactureAchat>;
    reminderPublisher?: ReminderPublisherApiObject;
};

/**
 * Component for displaying the list of invoices associated with a reminder
 *
 * Uses StoreDatatable to display invoices with pagination and sorting
 */
const InvoicesList: React.FunctionComponent<InvoicesListProps> = function ({
    dataSource,
    fetchInvoices,
    reminderPublisher,
}) {
    const { palette } = useL2DTheme();
    const { dissociateInvoiceReminderPublisher, dissociateMultipleInvoiceReminderPublisher } =
        useDissociateInvoiceReminderPublisher();

    const isDisabled = React.useMemo(
        () => reminderPublisher?.status === 'sent',
        [reminderPublisher?.status],
    );

    const listItemActions = [
        {
            label: 'Éligibilité',
            onClickCell: () => {},
            render: (factureAchat: FactureAchat) => (
                <SingleInvoiceEligibilityInfo
                    factureAchat={factureAchat}
                    reminderPublisher={reminderPublisher}
                />
            ),
            isDisplayed: true,
        },
        {
            label: 'Actions',
            onClickCell: () => {},
            render: (factureAchat: FactureAchat) => (
                <Stack
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                    }}
                >
                    <FlexyIconButton
                        displayTooltip
                        icon={dissociateIcon}
                        onClick={() => {
                            if (!reminderPublisher) return;

                            dissociateInvoiceReminderPublisher(
                                reminderPublisher,
                                factureAchat,
                                fetchInvoices,
                            );
                        }}
                        tooltipOverwriteProps={{
                            title: 'Dissocier cette facture',
                        }}
                        iconOverwriteProps={{
                            color: palette.secondary.main,
                            size: 'xs',
                        }}
                        disabled={isDisabled}
                    />
                </Stack>
            ),
            isDisplayed: true,
        },
    ];

    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    return (
        <Card
            sx={{
                marginBottom: 2,
                marginTop: 0,
                flex: 1,
                height: `calc(100vh - ${footerHeight}px - ${TopbarHeight}px - 45px - 95px)`,
            }}
        >
            <Box sx={{ padding: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: 1,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ marginBottom: 2 }}
                        >
                            Factures associées ({dataSource.pagination.total || 0})
                        </Typography>

                        <GlobalEligibilitySummary reminderPublisher={reminderPublisher} />
                    </Box>
                    <AssociateInvoiceButton
                        reminderPublisher={reminderPublisher}
                        dataSource={dataSource}
                        fetchData={fetchInvoices}
                    />
                </Box>

                {dataSource.data?.length ? (
                    <StoreDatatable
                        dataSource={dataSource}
                        columns={[...invoiceColumns, ...listItemActions]}
                        fetchData={fetchInvoices}
                        localStorageKey="ReminderPublisherDetailFactures"
                        onClickRow={(e, factureAchat) => {
                            if (!factureAchat) return;

                            navigateToFacture({
                                factureAchat,
                                navigate,
                                onError: (message) => {
                                    enqueueSnackbar(<Typography>{message}</Typography>, {
                                        variant: 'warning',
                                    });
                                },
                                openNewTab: true,
                            });
                        }}
                        rowsActions={
                            reminderPublisher?.status !== 'sent'
                                ? [
                                      {
                                          icon: 'fa-link-slash',
                                          label: 'Dissocier les factures',
                                          onClick: (invoices: Array<FactureAchat>) => {
                                              if (!reminderPublisher) return;

                                              dissociateMultipleInvoiceReminderPublisher(
                                                  reminderPublisher,
                                                  invoices,
                                                  fetchInvoices,
                                              );
                                          },
                                      },
                                  ]
                                : undefined
                        }
                    />
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1,
                            minHeight: '100px',
                        }}
                    >
                        <Typography
                            variant="body1"
                            color="text.secondary"
                        >
                            Aucune facture associée à ce rappel
                        </Typography>
                    </Box>
                )}
            </Box>
        </Card>
    );
};

export default InvoicesList;
